import { Controller, useForm } from 'react-hook-form';

import { useOnboardingContext } from '@/contexts/onboardingContext';
import useTranslate from '@/hooks/useTranslate';
import { useTreatableTreatmentPreferences } from '@/pages/members/hooks/useTreatableTreatmentPreferences';
import { STEPS_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import StepComponent from '@/pages/members/pages/Onboarding/steps/StepComponent';
import { StepProps } from '@/pages/members/pages/Onboarding/steps/types';
import {
  Needs,
  PrimarySubstanceFormValues
} from '@/pages/members/pages/Onboarding/steps/WhatDoYouNeedHelpWithStep/types';
import getSubmittedData from '@/pages/members/pages/Onboarding/utils/getSubmittedData';
import { TreatableTreatmentPreference } from '@/repositories/members/ProgramRepository';
import Alert from '@/ui/members/components/Alert';
import FieldError from '@/ui/members/components/form/FieldError';
import InputGroup from '@/ui/members/components/form/InputGroup';
import RadioGroup from '@/ui/members/components/form/RadioGroup';
import Spinner from '@/ui/members/components/Spinner';
import Heading from '@/ui/members/components/typography/Heading/Heading';

import { TreatmentPreferenceFormValues } from './types';

interface Props extends StepProps {
  /**
   * Whether to show all content or a minimal version
   */
  showAllContent?: boolean;
}

const HowCanWeHelpStep = ({ nodeType, showAllContent = true, uid }: Props) => {
  const onboardingContext = useOnboardingContext();
  const {
    data: { drugCategory, jurisdiction, programScope },
    formOptions
  } = onboardingContext;
  const { t } = useTranslate(`${STEPS_PATH_TRANSLATION_KEY}.HowCanWeHelpStep`);

  const mentalHealthTreatablePreferenceLabelMap: { [key: string]: string } = {
    counseling_therapy_only: t('mentalHealth.counseling_therapy_only'),
    medication_only: t('mentalHealth.medication_only'),
    counseling_therapy_and_meds: t('mentalHealth.counseling_therapy_and_meds')
  };
  const substanceUseTreatablePreferenceLabelMap: { [key: string]: string } = {
    counseling_therapy_only: t('substanceUse.counseling_therapy_only'),
    medication_only: t('substanceUse.medication_only'),
    counseling_therapy_and_meds: t('substanceUse.counseling_therapy_and_meds')
  };

  const treatmentPreferenceLabel = (preference: TreatableTreatmentPreference) => {
    if (programScope === Needs.MentalHealth) {
      return mentalHealthTreatablePreferenceLabelMap[preference.value] || preference.label;
    } else if (programScope === Needs.SubstanceUse) {
      return substanceUseTreatablePreferenceLabelMap[preference.value] || preference.label;
    } else {
      return preference.label;
    }
  };

  const { isError, isPending } = useTreatableTreatmentPreferences({
    drug_category: drugCategory as string,
    jurisdiction: jurisdiction as string
  });

  const treatmentPreferenceOptions =
    formOptions.treatableTreatmentPreferences?.map((preference) => ({
      label: treatmentPreferenceLabel(preference),
      value: preference.value
    })) || [];

  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<PrimarySubstanceFormValues>({
    mode: 'onSubmit',
    defaultValues: (getSubmittedData(
      nodeType,
      uid,
      onboardingContext.steps
    ) as TreatmentPreferenceFormValues) || {
      treatmentPreference: ''
    }
  });

  return (
    <StepComponent handleSubmit={handleSubmit} uid={uid} nodeType={nodeType} isValid={isValid}>
      {isError && (
        <Alert level="error">
          <p>{t('errorFetching')}</p>
        </Alert>
      )}

      <div className="text-center mb-4">
        <Heading className="mb-3" value={1}>
          {t('heading')}
        </Heading>

        <p className="text-base">{showAllContent ? t('intro') : t('introMinimal')}</p>
      </div>

      <InputGroup>
        <Controller
          name="treatmentPreference"
          rules={{
            required: t('treatmentPreferenceIsRequired')
          }}
          control={control}
          render={({ field }) => (
            <>
              {isPending ? (
                <Spinner />
              ) : (
                <RadioGroup
                  autoFocus
                  optionsClassName="grid gap-2 mb-6"
                  id="treatmentPreference"
                  onChange={field.onChange}
                  options={treatmentPreferenceOptions}
                  selectedValue={field.value as string}
                />
              )}
            </>
          )}
        />
        <FieldError error={errors.treatmentPreference} />
      </InputGroup>
    </StepComponent>
  );
};

export default HowCanWeHelpStep;
